import { useTranslation } from 'react-i18next';
import Loader from '@/components/common/Loader';
import PrimaryButton from '@/components/common/PrimaryButton';
import RadioUpload from '@/components/common/RadioUpload';
import { BulkActionEnum, type BulkAction } from '../useBulkModificationState';

interface ChooseMethodStepProps {
    isProcessing: boolean;
    action: BulkAction;
    onActionChange: (action: BulkAction) => void;
    onFileChange: (file: File | null) => void;
    onDownloadTemplate?: () => void;
    errorMessage?: string;
    bannerTitle?: string;
    bannerDescription?: string;
    notificationMessage?: React.ReactNode;
}

const ChooseMethodStep = ({
    isProcessing,
    action,
    onActionChange,
    onFileChange,
    onDownloadTemplate,
    errorMessage,
    notificationMessage,
}: ChooseMethodStepProps) => {
    const { t } = useTranslation('performanceResult');

    const options = [
        {
            value: BulkActionEnum.Import,
            label: (
                <div className="flex flex-col items-start">
                    <div className="flex items-center gap-2">
                        <span className="text-base font-semibold text-neutral-800">{t('bulk.importDataLabel')}</span>
                    </div>
                </div>
            ),
            description: t('bulk.importDataDescription'),
            upload: true,
        },
        {
            value: BulkActionEnum.Export,
            label: (
                <div className="flex flex-col items-start">
                    <div className="flex items-center gap-2">
                        <span className="text-base font-semibold text-neutral-800">{t('bulk.exportDataLabel')}</span>
                    </div>
                </div>
            ),
            description: t('bulk.exportDataDescription'),
        },
    ];

    if (isProcessing) {
        return <Loader />;
    }

    return (
        <div>
            <div className="mb-3 text-base font-medium text-neutral-500">
                <span className="pr-2">{t('bulk.firstTimeImporting')}</span>
                <PrimaryButton
                    onClick={onDownloadTemplate}
                    aria-label={t('bulk.downloadTemplate')}
                    className="h-auto bg-transparent p-0 text-base font-semibold text-blue-600 shadow-none hover:cursor-pointer hover:underline"
                >
                    {t('bulk.downloadTemplate')}
                </PrimaryButton>
            </div>

            {notificationMessage && <div className="mb-5">{notificationMessage}</div>}

            <RadioUpload
                required
                name="bulkMethod"
                options={options}
                className="col-span-2"
                onFileChange={onFileChange}
                onChange={val => onActionChange(val as BulkAction)}
                value={action ?? undefined}
                errorMessage={errorMessage}
                accept={'.xlsx,.xls,.csv'}
            />
        </div>
    );
};

export default ChooseMethodStep;
